'use client';

import { useCallback, useMemo, useSyncExternalStore } from 'react';

const HOVER_CAPABLE_QUERY = '(hover: hover) and (pointer: fine)';

function getMediaQueryList(query: string) {
  return typeof window === 'undefined' ||
    typeof window.matchMedia !== 'function'
    ? null
    : window.matchMedia(query);
}

/**
 * Whether `query` currently matches, kept in sync as the viewport or input
 * device changes. Returns `fallback` while server-rendering and in
 * environments without `matchMedia`.
 */
export function useMediaQuery(query: string, fallback = false) {
  const list = useMemo(() => getMediaQueryList(query), [query]);

  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      list?.addEventListener('change', onStoreChange);
      return () => list?.removeEventListener('change', onStoreChange);
    },
    [list],
  );

  const getSnapshot = useCallback(
    () => list?.matches ?? fallback,
    [list, fallback],
  );

  const getServerSnapshot = useCallback(() => fallback, [fallback]);

  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}

/**
 * Whether the primary input device can hover — false on phones and tablets.
 * Assumes it can when unknown, so hover-driven UI is never lost to a
 * capability check that could not run.
 */
export function useHoverCapability() {
  return useMediaQuery(HOVER_CAPABLE_QUERY, true);
}
